import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { requestPasswordResetSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { trpc } from "../../trpc";
import { Button } from "../shared/Button";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";
import { AuthLayout } from "./AuthLayout";

export const ForgotPasswordPage = () => {
  const form = useFormStyles();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [sentTo, setSentTo] = useState<string | undefined>(undefined);
  const request = useAction(
    useCallback(
      (input: { email: string }) =>
        trpc.auth.requestPasswordReset.mutate(input).then(() => true),
      []
    )
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = requestPasswordResetSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      return;
    }
    setFieldError(undefined);
    if (await request.run(parsed.data)) {
      setSentTo(parsed.data.email);
    }
  };

  return (
    <AuthLayout width={420}>
      <h2>Reset your password</h2>
      {sentTo ? (
        <>
          <Notice kind="success">
            {`If ${sentTo} has a VetBoats account, we've emailed it a link to choose a new password. The link expires in an hour.`}
          </Notice>
          <p>
            Didn't get it? Check your spam folder, or wait a couple of minutes
            and try again.
          </p>
        </>
      ) : (
        <>
          <p>
            Enter the email you use for VetBoats and we'll send you a link to
            choose a new password.
          </p>
          {request.error && <Notice kind="error">{request.error}</Notice>}
          <form
            onSubmit={(e) => void submit(e)}
            noValidate
            className={form.stack}
          >
            <Field label="Email" htmlFor="forgot-email" error={fieldError}>
              <input
                id="forgot-email"
                type="email"
                className={form.control}
                autoComplete="email"
                autoCapitalize="none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <div className={form.actions}>
              <Button type="submit" disabled={request.pending || !email}>
                {request.pending ? "Sending…" : "Send reset link"}
              </Button>
            </div>
          </form>
        </>
      )}
      <p className={form.finePrint}>
        <a href="#/">Back to sign in</a>
      </p>
    </AuthLayout>
  );
};
